import { DEFAULT_LANGUAGE } from '@/constants';
import { type IFooter, LanguageCode } from '@/interfaces';
import { getMasterTranslationLanguageCode, languageFieldsMapping, translationDataModify } from '@/utils';
import callContentful from './base.fetchers';

type GetFooter = (args: {
  isPreviewMode: boolean;
  languageCode?: LanguageCode;
  isMultiLanguagePage: boolean;
  slug: string;
}) => Promise<IFooter[]>;

export const getFooter: GetFooter = async ({
  isPreviewMode,
  languageCode = LanguageCode.English,
  isMultiLanguagePage = false,
  slug,
}) => {
  const mappedLanguageFields = languageFieldsMapping(languageCode);
  const languageField = getMasterTranslationLanguageCode(languageCode);

  const query = `
    {
      defaultFooterCollection: footerCollection(
        preview: ${isPreviewMode}
        locale: "${DEFAULT_LANGUAGE}"
        where: { forPmtPage: ${isMultiLanguagePage} }
        limit: 1
      ) {
        items {
          sys {
            id
          }
          subText {
            en
          }
          itemList
          pageLinks
          copyright {
            en
          }
          ppText {
            en
          }
        }
      }
      footerCollection(
        preview: ${isPreviewMode}
        locale: "${mappedLanguageFields.locale}"
        where: { forPmtPage: ${isMultiLanguagePage} }
        limit: 10
      ) {
        items {
          subText {
            ${languageField}
          }
          itemList
          pageLinks
          copyright {
            ${languageField}
          }
          ppText {
            ${languageField}
          }
          appBadgeMainText {
            ${languageField}
          }
          appBadgeSubText {
            ${languageField}
          }
          appStoreUrl
          googlePlayUrl
          appBadgeCountry
        }
      }
    }
  `;

  const response = await callContentful(query, isPreviewMode, slug, 'footer.fetchers.getFooter');

  const defaultFooterData = response?.data?.defaultFooterCollection?.items?.filter(Boolean)[0] || null;
  const rawFooterData = response?.data?.footerCollection?.items?.filter(Boolean) || null;

  // Transform the linked multiLanguageField data to flat string values
  const footerData = rawFooterData?.map((footer: Record<string, unknown>) => {
    const subText = footer.subText as Record<string, string> | null;
    const copyright = footer.copyright as Record<string, string> | null;
    const ppText = footer.ppText as Record<string, string> | null;
    const appBadgeMainText = footer.appBadgeMainText as Record<string, string> | null;
    const appBadgeSubText = footer.appBadgeSubText as Record<string, string> | null;

    // Also transform defaultFooterData fields
    let transformedDefaultFooterData = defaultFooterData;
    if (defaultFooterData) {
      const defaultSubText = defaultFooterData.subText as Record<string, string> | null;
      const defaultCopyright = defaultFooterData.copyright as Record<string, string> | null;
      const defaultPpText = defaultFooterData.ppText as Record<string, string> | null;

      transformedDefaultFooterData = {
        ...defaultFooterData,
        subText: defaultSubText?.en || '',
        copyright: defaultCopyright?.en || '',
        ppText: defaultPpText?.en || '',
      };
    }

    const transformedFooter = {
      ...footer,
      subText: subText?.[languageField] || '',
      copyright: copyright?.[languageField] || '',
      ppText: ppText?.[languageField] || '',
      appBadgeMainText: appBadgeMainText?.[languageField] || '',
      appBadgeSubText: appBadgeSubText?.[languageField] || '',
    };

    return translationDataModify(transformedDefaultFooterData, transformedFooter, [
      'appBadgeMainText',
      'appBadgeSubText',
      'appStoreUrl',
      'googlePlayUrl',
      'appBadgeCountry',
    ]);
  });

  return footerData || [];
};
